import { programDayKey, programDayKeys, todayProgramDayKey } from '@/helpers/program-day';
import { dayKeyOfLocalDate, localDateOfDayKey, zonedInstant } from '@cineamo/ui/src/helpers/cinema-time-zone';

/**
 * Functions, not constants: a warm server instance evaluates this module once and would keep
 * serving the day it booted on, while the browser re-evaluates it on every page load. Server and
 * client then render different days and hydration of the program page fails (CIN-14426).
 */
export function dateToday(): Date {
    return new Date();
}

const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;

export function dateMinus(date: Date, days = 0, hours = 0, minutes = 0, seconds = 0) {
    const newDate = new Date(date);
    if (days !== 0) {
        newDate.setDate(newDate.getDate() - days);
    }
    if (hours !== 0 || minutes !== 0 || seconds !== 0) {
        newDate.setTime(newDate.getTime() - (1000 * seconds + 1000 * 60 * minutes + 1000 * 60 * 60 * hours));
    }
    return newDate;
}

export type DateTimeFormatOptions = Intl.DateTimeFormatOptions;

export function formatDate(
    date: string | Date,
    locales: string = 'de-DE',
    options: DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }
): string {
    const tmpDate: Date = new Date(date);
    // A missing or unparseable date renders empty; Intl throws on an invalid Date and takes the page down.
    if (!Number.isNaN(tmpDate.getTime())) {
        return new Intl.DateTimeFormat(locales, options).format(tmpDate);
    }
    return '';
}

export function formatDay(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                weekday: 'short'
            }
        );
    }
    return '';
}

export function formatDayNumeric(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                day: 'numeric'
            }
        );
    }
    return '';
}

export function formatMonthLong(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                month: 'long'
            }
        );
    }
    return '';
}

export function formatYearNumeric(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                year: 'numeric'
            }
        );
    }
    return '';
}

export function formatTime(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(date, locales, {
        hour: 'numeric',
        minute: 'numeric',
        ...options
    });
}

export function formatDateTime(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(
        date,
        locales,
        options || {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }
    );
}

export function formatDateNumbersOnly(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(
        date,
        locales,
        options || {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }
    );
}

export function formatDateHyphenSeparatedYearMonthDay(date: string | Date): string {
    return formatDate_yyyyMMdd(date);
}

export function formatDate_HHmm24(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

export function formatDateForMyRequestsList(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).replace('.,', ',');
}

export function formatDayMonth(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        day: '2-digit',
        month: '2-digit'
    });
}

export function formatDateWeekdayYearMonthDay(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).replace('.,', ',');
}

export function formatDateWeekdayYearMonthDayHourMinute(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).replace('.,', ',');
}

export function getAge(dateString: string | Date): number {
    const birthDate = new Date(dateString);
    const today = dateToday();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}

export function createServerDate(value: string | Date) {
    const valueAsDateString = new Date(value);
    valueAsDateString.setUTCHours(0, 0, 0, 0);
    const isoString = valueAsDateString.toISOString();
    return isoString.replace('.000Z', 'Z');
}

export function formatDate_yyyyMMdd(value: Date | string): string {
    const date = new Date(value);
    if (isNaN(Number(date))) {
        return '';
    }
    const localDate = new Date(date.getTime());
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function formatDate_yyyyMMddTHHmmssZ(value: Date): string {
    const isoString = value.toISOString();
    return isoString.substring(0, 19) + 'Z';
}

export const isDateFuture = (date: string | Date): boolean => {
    if (!date) {
        return false;
    }
    return new Date(date) > dateToday();
};

/**
 * Whether `date` is today on the cinema's clock. A string is a day key or an instant
 * (`programDayKey`); a `Date` is a calendar bar day, which carries its day in its process-zone fields
 * (`localDateOfDayKey`).
 */
export const isDateToday = (date: string | Date, timeZone: string): boolean => {
    if (!date) {
        return false;
    }
    const dayKey = typeof date === 'string' ? programDayKey(date, timeZone) : dayKeyOfLocalDate(date);
    return dayKey === todayProgramDayKey(timeZone);
};

export function getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

export function getWeeksBetweenDates(
    startDate: Date,
    endDate: Date
): { weekNumber: number; startDate: Date; endDate: Date }[] {
    const weeks: { weekNumber: number; startDate: Date; endDate: Date }[] = [];

    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        const weekStart = new Date(currentDate);
        const weekNumber = getWeekNumber(currentDate);
        currentDate.setDate(currentDate.getDate() + 6); // move to the end of the week
        const weekEnd = currentDate > endDate ? new Date(endDate) : new Date(currentDate);

        weeks.push({
            weekNumber,
            startDate: weekStart,
            endDate: weekEnd
        });

        currentDate.setDate(currentDate.getDate() + 1); // move to the next week's start
    }

    return weeks;
}

export function getDateFourWeeksAgo() {
    const today = new Date();
    today.setDate(today.getDate() - 28);
    return today;
}

/**
 * The days the calendar bar renders, as `Date`s that *name* the cinema's calendar days.
 *
 * The days come from the cinema's clock (`programDayKeys`), but are handed back as process-zone
 * midnights so that `formatDate_yyyyMMdd` and `dayKeyOfLocalDate` read the day that was meant. They
 * are not instants — never send one to the API or compare it with a showing's `startDatetime`; use
 * `programDayStart`/`programDayEnd` for that. To print one with `useFormat` (which formats on the
 * cinema's clock, not the browser's), pass it through `calendarBarLabelInstant` first.
 */
export function getDates(timeZone: string, visibleDaysInFuture = 14): Date[] {
    return programDayKeys(timeZone, visibleDaysInFuture).map(localDateOfDayKey);
}

/**
 * Midday at the cinema on the day a `getDates` entry names — the instant to hand a formatter that
 * uses the cinema's zone. Formatting the process-zone midnight itself names the previous day for a
 * viewer east of the cinema.
 */
export function calendarBarLabelInstant(date: Date, timeZone: string): Date {
    return zonedInstant(dayKeyOfLocalDate(date), 12, timeZone);
}

/**
 * Whether a yyyy-MM-dd date is one of the days the calendar bar renders.
 * Dates beyond that window are only reachable through the date picker, so the
 * bar cannot show which day is selected and the showings have to say it themselves.
 *
 * Deliberately derived from `getDates()` rather than from day arithmetic: it keeps the
 * window defined in exactly one place (the bar renders the same array). The cost is one
 * 14-element array per call, which is meant to be paid once per component render — do not
 * call it per list item.
 */
export function isDateWithinCalendarBar(
    date: string | undefined,
    timeZone: string,
    visibleDaysInFuture?: number
): boolean {
    return getDates(timeZone, visibleDaysInFuture).some((visibleDate) => formatDate_yyyyMMdd(visibleDate) === date);
}

export function dateAsStringOrNull(date: string | Date): string | null {
    return date ? formatDate_yyyyMMddTHHmmssZ(new Date(date)) : null;
}

export function daysBetween(date1: Date, date2: Date) {
    return Math.floor(Math.abs((date1.getTime() - date2.getTime()) / MILLISECONDS_IN_DAY));
}

export const getDateInTimezone = (date: string | Date, timezone: string = 'Europe/Berlin') => {
    return new Date(new Date(date).toLocaleString('en-US', { timeZone: timezone }));
};
